import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { Tab } from '@headlessui/react';
import {
    BanknotesIcon,
    ArrowDownTrayIcon,
    ArrowUpTrayIcon,
    CreditCardIcon,
    CurrencyDollarIcon
} from '@heroicons/react/24/outline';
import customerAccountService from '../../services/customerAccountService';
import currencyService from '../../services/currencyService';
import cashDrawerService from '../../services/cashDrawerService';

function classNames(...classes) {
    return classes.filter(Boolean).join(' ');
}

const CustomerWallet = ({ customerUuid }) => {
    const { t } = useTranslation();
    const [accounts, setAccounts] = useState([]);
    const [currencies, setCurrencies] = useState([]);
    const [drawers, setDrawers] = useState([]);
    const [loading, setLoading] = useState(false);

    // Form State
    const [amount, setAmount] = useState('');
    const [selectedCurrency, setSelectedCurrency] = useState('');
    const [selectedDrawer, setSelectedDrawer] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('cash'); // 'cash', 'fib', 'qicard'
    const [notes, setNotes] = useState('');
    const [submitting, setSubmitting] = useState(false);

    // Fetch Data
    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const [accountsData, currenciesData, drawersData] = await Promise.all([
                customerAccountService.getCustomerAccounts(customerUuid).catch(e => ({ success: false, data: { accounts: [] } })),
                currencyService.getCurrencies().catch(e => ({ success: false, data: [] })),
                cashDrawerService.getDrawers({ active: true }).catch(e => ({ success: false, data: [] }))
            ]);
            
            const accs = accountsData.accounts || accountsData.data?.accounts || [];
            const currs = currenciesData.data || (Array.isArray(currenciesData) ? currenciesData : []);
            const drws = drawersData.data || (Array.isArray(drawersData) ? drawersData : []);

            setAccounts(accs);
            setCurrencies(Array.isArray(currs) ? currs : []);
            setDrawers(Array.isArray(drws) ? drws : []);
            
            // Set default currency if not set
            if (currs.length > 0 && !selectedCurrency) {
                setSelectedCurrency(currs[0].id.toString());
            }
            
            if (drws.length > 0 && !selectedDrawer) {
                setSelectedDrawer(drws[0].id.toString());
            }
        } catch (error) {
            console.error('Error fetching wallet data:', error);
            toast.error(t('errors.fetchFailed'));
        } finally {
            setLoading(false);
        }
    }, [customerUuid, t, selectedCurrency, selectedDrawer]);

    useEffect(() => {
        if (customerUuid) {
            fetchData();
        }
    }, [customerUuid, fetchData]);

    const handleSubmit = async (action) => {
        if (!amount || parseFloat(amount) <= 0) {
            toast.error(t('validation.positiveAmount'));
            return;
        }
        if (!selectedCurrency) {
            toast.error(t('validation.selectCurrency'));
            return;
        }
        if (paymentMethod === 'cash' && !selectedDrawer) {
            toast.error(t('validation.selectDrawer', 'Please select a cash drawer'));
            return;
        }

        try {
            setSubmitting(true);
            const data = {
                currencyId: selectedCurrency,
                amount,
                method: paymentMethod,
                notes,
                drawerId: paymentMethod === 'cash' ? selectedDrawer : null
            };

            let response;
            // For Withdraw/Repay we might need accountUuid if the API requires it, 
            // but our updated service/controller usually finds account by customer + currency or creaets it.
            // Actually, controller method `withdraw` and `repayLoan` expect `uuid` param which is Account UUID.
            // So we need to find the account UUID for the selected currency first.

            const account = accounts.find(a => a.currencyId === parseInt(selectedCurrency)); // Loose equality for string/number match

            if ((action === 'withdraw' || action === 'repay') && !account) {
                toast.error(t('errors.accountNotFound')); // Can't withdraw/repay if no account exists yet
                return;
            }

            if (action === 'deposit') {
                response = await customerAccountService.deposit(customerUuid, data);
            } else if (action === 'withdraw') {
                response = await customerAccountService.withdraw(account.uuid, data);
            } else if (action === 'loan') {
                response = await customerAccountService.giveLoan(customerUuid, data);
            } else if (action === 'repay') {
                response = await customerAccountService.repayLoan(account.uuid, data);
            }

            toast.success(response.message || t('success.operationCompleted'));
            setAmount('');
            setNotes('');
            fetchData(); // Refresh data
        } catch (error) {
            console.error('Wallet operation error:', error);
            toast.error(error.response?.data?.message || t('errors.operationFailed'));
        } finally {
            setSubmitting(false);
        }
    };

    const categories = {
        deposit: { name: t('wallet.deposit'), icon: ArrowDownTrayIcon, action: 'deposit', color: 'text-green-600', bg: 'bg-green-100' },
        withdraw: { name: t('wallet.withdraw'), icon: ArrowUpTrayIcon, action: 'withdraw', color: 'text-red-600', bg: 'bg-red-100' },
        loan: { name: t('wallet.giveLoan'), icon: BanknotesIcon, action: 'loan', color: 'text-amber-600', bg: 'bg-amber-100' },
        repay: { name: t('wallet.repayLoan'), icon: CurrencyDollarIcon, action: 'repay', color: 'text-blue-600', bg: 'bg-blue-100' },
    };

    return (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 mb-6">
            <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white mb-4 flex items-center">
                <CreditCardIcon className="h-5 w-5 mr-2 text-primary-600" />
                {t('wallet.title', 'Customer Wallet')}
            </h3>

            {/* Account Summaries */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                {accounts.map(account => (
                    <div key={account.uuid} className="bg-gray-50 dark:bg-gray-700 p-4 rounded-md border border-gray-200 dark:border-gray-600">
                        <div className="flex justify-between items-center mb-2">
                            <span className="font-bold text-gray-700 dark:text-gray-200">{account.currencyCode}</span>
                        </div>
                        <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-500 dark:text-gray-400">{t('wallet.balance', 'Balance')}:</span>
                            <span className={`font-medium ${account.balance >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                {parseFloat(account.balance).toLocaleString()} {account.currencySymbol}
                            </span>
                        </div>
                        <div className="flex justify-between text-sm">
                            <span className="text-gray-500 dark:text-gray-400">{t('wallet.loan', 'Loan')}:</span>
                            <span className="font-medium text-red-600 dark:text-red-400">
                                {parseFloat(account.loanBalance).toLocaleString()} {account.currencySymbol}
                            </span>
                        </div>
                    </div>
                ))}
                {accounts.length === 0 && !loading && (
                    <div className="col-span-full text-center text-gray-500 dark:text-gray-400 py-4 text-sm">
                        {t('wallet.noAccounts', 'No active wallet accounts.')}
                    </div>
                )}
            </div>

            {/* Action Tabs */}
            <Tab.Group>
                <Tab.List className="flex space-x-1 rounded-xl bg-gray-100 dark:bg-gray-700 p-1 mb-4">
                    {Object.values(categories).map((category) => (
                        <Tab
                            key={category.name}
                            className={({ selected }) =>
                                classNames(
                                    'w-full rounded-lg py-2.5 text-sm font-medium leading-5',
                                    'ring-white/60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2',
                                    selected
                                        ? 'bg-white dark:bg-gray-600 text-primary-700 dark:text-primary-300 shadow'
                                        : 'text-gray-600 dark:text-gray-400 hover:bg-white/[0.12] hover:text-gray-800'
                                )
                            }
                        >
                            <div className="flex items-center justify-center">
                                <category.icon className="w-5 h-5 mr-2" aria-hidden="true" />
                                {category.name}
                            </div>
                        </Tab>
                    ))}
                </Tab.List>
                <Tab.Panels>
                    {Object.values(categories).map((category, idx) => (
                        <Tab.Panel
                            key={idx}
                            className={classNames(
                                'rounded-xl bg-white dark:bg-gray-800',
                                'ring-white/60 ring-offset-2 ring-offset-blue-400 focus:outline-none'
                            )}
                        >
                            <div className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            {t('common.currency')}
                                        </label>
                                        <select
                                            value={selectedCurrency}
                                            onChange={(e) => setSelectedCurrency(e.target.value)}
                                            className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                                        >
                                            <option value="">{t('common.select', 'Select...')}</option>
                                            {currencies.map(c => (
                                                <option key={c.id} value={c.id}>{c.code} - {c.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            {t('common.paymentMethod', 'Payment Method')}
                                        </label>
                                        <select
                                            value={paymentMethod}
                                            onChange={(e) => setPaymentMethod(e.target.value)}
                                            className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                                        >
                                            <option value="cash">{t('common.cash', 'Cash (Manual)')}</option>
                                            <option value="fib">FIB</option>
                                            <option value="qicard">QI Card</option>
                                        </select>
                                    </div>
                                </div>
                                
                                {paymentMethod === 'cash' && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            {t('common.cashDrawer', 'Cash Drawer')}
                                        </label>
                                        <select
                                            value={selectedDrawer}
                                            onChange={(e) => setSelectedDrawer(e.target.value)}
                                            className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                                        >
                                            <option value="">{t('common.selectDrawer', 'Select a drawer')}</option>
                                            {drawers.map(d => (
                                                <option key={d.id} value={d.id}>{d.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        {t('common.amount')}
                                    </label>
                                    <div className="relative mt-1 rounded-md shadow-sm">
                                        <input
                                            type="number"
                                            value={amount}
                                            onChange={(e) => setAmount(e.target.value)}
                                            className="block w-full rounded-md border-gray-300 dark:border-gray-600 pl-3 pr-12 focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                                            placeholder="0.00"
                                            min="0"
                                            step="any"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        {t('common.notes')}
                                    </label>
                                    <textarea
                                        rows={2}
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                        className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                                    />
                                </div>

                                <div className="flex justify-end pt-2">
                                    <button
                                        onClick={() => handleSubmit(category.action)}
                                        disabled={submitting}
                                        className={`inline-flex justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 ${category.action === 'withdraw' || category.action === 'loan'
                                                ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                                                : 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
                                            } ${submitting ? 'opacity-75 cursor-not-allowed' : ''}`}
                                    >
                                        {submitting ? t('common.processing', 'Processing...') : category.name}
                                    </button>
                                </div>
                            </div>
                        </Tab.Panel>
                    ))}
                </Tab.Panels>
            </Tab.Group>
        </div>
    );
};

export default CustomerWallet;
